import { useState, useEffect, useCallback } from "react";
import { Settings, Lock, Store, Clock, CreditCard, Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";
import { FiscalSettings } from "@/components/FiscalOrderDialog";

// Seletor de horário 24h (evita o AM/PM do input type="time" em alguns navegadores)
function TimePicker24h({ value, onChange }) {
  const [h, m] = (value || "00:00").split(":");
  const hours   = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
  const minutes = ["00", "15", "30", "45"];

  const update = (newH, newM) => onChange(`${newH}:${newM}`);

  return (
    <div className="flex items-center gap-1">
      <select
        value={h}
        onChange={(e) => update(e.target.value, m)}
        className="bg-input border border-transparent focus:border-primary rounded-md h-10 px-2 text-sm text-foreground cursor-pointer"
      >
        {hours.map((hour) => (
          <option key={hour} value={hour}>{hour}h</option>
        ))}
      </select>
      <span className="text-muted-foreground text-sm">:</span>
      <select
        value={m}
        onChange={(e) => update(h, e.target.value)}
        className="bg-input border border-transparent focus:border-primary rounded-md h-10 px-2 text-sm text-foreground cursor-pointer"
      >
        {minutes.map((min) => (
          <option key={min} value={min}>{min}</option>
        ))}
      </select>
    </div>
  );
}

const DIAS_SEMANA = [
  { value: 0, label: "Dom" },
  { value: 1, label: "Seg" },
  { value: 2, label: "Ter" },
  { value: 3, label: "Qua" },
  { value: 4, label: "Qui" },
  { value: 5, label: "Sex" },
  { value: 6, label: "Sab" },
];

export default function SettingsPage() {
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  // ── Tab Loja ────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [storeForm, setStoreForm] = useState({
    nome: "",
    whatsapp: "",
    horario_abertura: "18:00",
    horario_fechamento: "22:30",
    dias_fechamento: [1],
    pix_chave: "",
    pix_nome: "",
    pix_banco: "",
  });

  // ── Tab Perfil ──────────────────────────────────────────
  const [passwordForm, setPasswordForm] = useState({
    current: "",
    new_pwd: "",
    confirm: "",
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new_pwd: false,
    confirm: false,
  });

  // ── Fetch store settings ────────────────────────────────
  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/store-settings");
      setStoreForm({
        nome: data.nome || "",
        whatsapp: data.whatsapp || "",
        horario_abertura: data.horario_abertura || "18:00",
        horario_fechamento: data.horario_fechamento || "22:30",
        dias_fechamento: data.dias_fechamento || [1],
        pix_chave: data.pix_chave || "",
        pix_nome: data.pix_nome || "",
        pix_banco: data.pix_banco || "",
      });
    } catch (err) {
      console.error("Erro ao carregar configurações:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // ── Salvar configurações da loja ────────────────────────
  async function handleSaveStore() {
    setSaving(true);
    try {
      await api.put("/store-settings", storeForm);
      alert("Configurações salvas com sucesso!");
    } catch (err) {
      alert(err.response?.data?.detail || "Erro ao salvar configurações");
    } finally {
      setSaving(false);
    }
  }

  // ── Alterar senha ───────────────────────────────────────
  async function handleChangePassword() {
    setPasswordError("");
    setPasswordSuccess("");

    if (!passwordForm.current || !passwordForm.new_pwd || !passwordForm.confirm) {
      setPasswordError("Preencha todos os campos.");
      return;
    }
    if (passwordForm.new_pwd.length < 6) {
      setPasswordError("A nova senha deve ter pelo menos 6 caracteres.");
      return;
    }
    if (passwordForm.new_pwd !== passwordForm.confirm) {
      setPasswordError("A nova senha e a confirmação não coincidem.");
      return;
    }

    setChangingPassword(true);
    try {
      await api.put("/auth/password", {
        current_password: passwordForm.current,
        new_password: passwordForm.new_pwd,
      });
      setPasswordSuccess("Senha alterada com sucesso!");
      setPasswordForm({ current: "", new_pwd: "", confirm: "" });
    } catch (err) {
      setPasswordError(err.response?.data?.detail || "Erro ao alterar senha");
    } finally {
      setChangingPassword(false);
    }
  }

  // ── Toggle dia de fechamento ────────────────────────────
  function toggleDia(dow) {
    setStoreForm((prev) => {
      const dias = prev.dias_fechamento.includes(dow)
        ? prev.dias_fechamento.filter((d) => d !== dow)
        : [...prev.dias_fechamento, dow].sort();
      return { ...prev, dias_fechamento: dias };
    });
  }

  // ── Render ──────────────────────────────────────────────
  return (
    <div className="space-y-6 max-w-2xl">
      {/* Header */}
      <div className="animate-fade-in-up stagger-1">
        <h1
          className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-foreground"
          style={{ fontFamily: "Oswald, sans-serif" }}
        >
          Configurações
        </h1>
        <p className="text-muted-foreground mt-1">
          Gerencie seu perfil e as configurações da loja
        </p>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="loja" className="animate-fade-in-up stagger-2">
        <TabsList className="bg-card border border-border">
          <TabsTrigger value="perfil" className="gap-1.5 data-[state=active]:bg-primary/15 data-[state=active]:text-primary">
            <Lock className="w-3.5 h-3.5" />
            Perfil
          </TabsTrigger>
          <TabsTrigger value="loja" className="gap-1.5 data-[state=active]:bg-primary/15 data-[state=active]:text-primary">
            <Store className="w-3.5 h-3.5" />
            Loja
          </TabsTrigger>
          <TabsTrigger value="fiscal">NFC-e</TabsTrigger>
        </TabsList>
        <TabsContent value="fiscal" className="mt-4"><FiscalSettings /></TabsContent>

        {/* ════════════ TAB PERFIL ════════════ */}
        <TabsContent value="perfil" className="mt-4 space-y-4">
          {/* Info do usuário */}
          <Card className="bg-card border-border">
            <CardContent className="pt-6 space-y-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center">
                  <Settings className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">{user.name || "Admin"}</p>
                  <p className="text-xs text-muted-foreground">{user.email || ""}</p>
                </div>
              </div>

              <Separator className="bg-border" />

              {/* Alterar Senha */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-muted-foreground" />
                  <h3 className="text-sm font-semibold text-foreground">Alterar Senha</h3>
                </div>

                <div className="space-y-3">
                  {[
                    { key: "current", label: "Senha atual" },
                    { key: "new_pwd", label: "Nova senha" },
                    { key: "confirm", label: "Confirmar nova senha" },
                  ].map(({ key, label }) => (
                    <div key={key} className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">{label}</Label>
                      <div className="relative">
                        <Input
                          type={showPasswords[key] ? "text" : "password"}
                          value={passwordForm[key]}
                          onChange={(e) =>
                            setPasswordForm((prev) => ({ ...prev, [key]: e.target.value }))
                          }
                          className="bg-input border-transparent focus:border-primary h-10 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowPasswords((prev) => ({ ...prev, [key]: !prev[key] }))
                          }
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPasswords[key] ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {passwordError && (
                  <p className="text-xs text-destructive">{passwordError}</p>
                )}
                {passwordSuccess && (
                  <p className="text-xs text-green-400">{passwordSuccess}</p>
                )}

                <Button
                  onClick={handleChangePassword}
                  disabled={changingPassword}
                  className="bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20"
                >
                  {changingPassword ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Alterando...
                    </>
                  ) : (
                    "Alterar Senha"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ════════════ TAB LOJA ════════════ */}
        <TabsContent value="loja" className="mt-4 space-y-4">
          {loading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="h-32 bg-card border border-border rounded-lg animate-pulse"
                />
              ))}
            </div>
          ) : (
            <>
              {/* Dados da Loja */}
              <Card className="bg-card border-border">
                <CardContent className="pt-6 space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Store className="w-4 h-4 text-muted-foreground" />
                    <h3 className="text-sm font-semibold text-foreground">Dados da Loja</h3>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Nome da Loja</Label>
                      <Input
                        value={storeForm.nome}
                        onChange={(e) =>
                          setStoreForm((prev) => ({ ...prev, nome: e.target.value }))
                        }
                        placeholder="Super Pizza"
                        className="bg-input border-transparent focus:border-primary h-10"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">WhatsApp</Label>
                      <Input
                        value={storeForm.whatsapp}
                        onChange={(e) =>
                          setStoreForm((prev) => ({ ...prev, whatsapp: e.target.value }))
                        }
                        placeholder="559812345678"
                        className="bg-input border-transparent focus:border-primary h-10"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Horário */}
              <Card className="bg-card border-border">
                <CardContent className="pt-6 space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <h3 className="text-sm font-semibold text-foreground">
                      Horário de Funcionamento
                    </h3>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Abertura</Label>
                      <TimePicker24h
                        value={storeForm.horario_abertura}
                        onChange={(v) => setStoreForm((prev) => ({ ...prev, horario_abertura: v }))}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Fechamento</Label>
                      <TimePicker24h
                        value={storeForm.horario_fechamento}
                        onChange={(v) => setStoreForm((prev) => ({ ...prev, horario_fechamento: v }))}
                      />
                    </div>
                  </div>

                  <Separator className="bg-border" />

                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">
                      Dias de fechamento
                    </Label>
                    <div className="flex flex-wrap gap-3">
                      {DIAS_SEMANA.map(({ value, label }) => {
                        const checked = storeForm.dias_fechamento.includes(value);
                        return (
                          <label
                            key={value}
                            className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors text-sm ${
                              checked
                                ? "bg-primary/15 text-primary border-primary/40"
                                : "text-muted-foreground border-border hover:bg-secondary/50"
                            }`}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => toggleDia(value)}
                            />
                            {label}
                          </label>
                        );
                      })}
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Marque os dias em que a loja estará fechada
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* PIX */}
              <Card className="bg-card border-border">
                <CardContent className="pt-6 space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="w-4 h-4 text-muted-foreground" />
                    <h3 className="text-sm font-semibold text-foreground">Dados PIX</h3>
                  </div>

                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Chave PIX</Label>
                      <Input
                        value={storeForm.pix_chave}
                        onChange={(e) =>
                          setStoreForm((prev) => ({ ...prev, pix_chave: e.target.value }))
                        }
                        placeholder="email@exemplo.com, CPF ou telefone"
                        className="bg-input border-transparent focus:border-primary h-10"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Nome</Label>
                        <Input
                          value={storeForm.pix_nome}
                          onChange={(e) =>
                            setStoreForm((prev) => ({ ...prev, pix_nome: e.target.value }))
                          }
                          placeholder="Super Pizza"
                          className="bg-input border-transparent focus:border-primary h-10"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Banco</Label>
                        <Input
                          value={storeForm.pix_banco}
                          onChange={(e) =>
                            setStoreForm((prev) => ({ ...prev, pix_banco: e.target.value }))
                          }
                          placeholder="Nubank, Inter, etc."
                          className="bg-input border-transparent focus:border-primary h-10"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Botão Salvar */}
              <Button
                onClick={handleSaveStore}
                disabled={saving}
                className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white font-semibold shadow-lg shadow-primary/20"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  "Salvar Alterações"
                )}
              </Button>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
